import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

import StepDisplay from './StepDisplay';
import AIBugAnalysisAccordion from './AIBugAnalysisAccordion';

export default function RunCardContent({
  run,
  showLogs,
  logsExpanded,
  setLogsExpanded,
  logs,
  isFetchingLogs,
  logsError,
  parsedFeatures,
  screenshotExpanded,
  setScreenshotExpanded,
  screenshotUrl,
  screenshotError,
}) {
  return (
    <>
      {/* Logs */}
      {showLogs ? (
        <Paper
          variant="outlined"
          sx={{
            bgcolor: 'custom.terminalBg',
            p: 2,
            mt: 1,
            maxHeight: 400,
            overflow: 'auto',
          }}
        >
          {logsError ? (
            <Typography color="error" sx={{ fontStyle: 'italic' }}>
              Failed to load logs.
            </Typography>
          ) : parsedFeatures.length > 0 ? (
            parsedFeatures.map((feature, fi) => (
              <Box key={fi} mb={2}>
                <Typography
                  sx={{ color: 'custom.terminalAccentPurple', fontWeight: 'bold', mb: 1 }}
                >
                  Feature: {feature.name}
                </Typography>

                {feature.scenarios.map((scenario, si) => (
                  <Box key={si} ml={2} mb={2}>
                    <Typography sx={{ color: 'custom.terminalAccentTeal', fontWeight: 500 }}>
                      Scenario: {scenario.name}
                    </Typography>

                    <Box ml={2}>
                      {scenario.steps.map((step, sti) => (
                        <StepDisplay key={sti} step={step} />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            ))
          ) : logs.length ? (
            <Box
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                color: 'custom.terminalText',
                whiteSpace: 'pre-wrap',
              }}
            >
              {logs.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {run.status === 'queued' ? 'Waiting for test to start...' : 'No logs available yet.'}
            </Typography>
          )}
        </Paper>
      ) : (
        <Accordion
          expanded={logsExpanded}
          onChange={(_, expanded) => setLogsExpanded(expanded)}
          disableGutters
          elevation={0}
          sx={{
            mt: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" justifyContent="space-between" width="100%">
              <Typography variant="subtitle2">Logs</Typography>
              {logsExpanded && isFetchingLogs && <CircularProgress size={14} />}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Paper
              variant="outlined"
              sx={{
                bgcolor: 'custom.terminalBg',
                p: 2,
                maxHeight: 300,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                color: 'custom.terminalText',
                whiteSpace: 'pre-wrap',
              }}
            >
              {logsError ? (
                <Typography color="error">Failed to load logs</Typography>
              ) : logs.length ? (
                logs.map((line, i) => <div key={i}>{line}</div>)
              ) : (
                <Typography color="text.secondary">No logs available.</Typography>
              )}
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Screenshot (failed runs only) */}
      {run.status === 'failed' && (
        <Accordion
          expanded={screenshotExpanded}
          onChange={(_, expanded) => setScreenshotExpanded(expanded)}
          disableGutters
          elevation={0}
          sx={{
            mt: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <PhotoCameraIcon fontSize="small" color="error" />
              <Typography variant="subtitle2">Screenshot</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {screenshotError ? (
              <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No screenshot available for this run.
              </Typography>
            ) : screenshotUrl ? (
              <Box
                component="img"
                src={screenshotUrl}
                alt="Failure screenshot"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 500,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            ) : (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}
      {/* AI Bug Analysis (failed runs only) */}
      {run.status === 'failed' && <AIBugAnalysisAccordion run={run} />}
    </>
  );
}
